import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { of, Subject } from 'rxjs';
import { catchError, take, takeUntil, timeout } from 'rxjs/operators';
import { ElectionService } from 'src/app/services/election.service';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { RDColumn } from 'src/app/models/RDTable';
import { MatSort } from '@angular/material/sort';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { IReportFirstPreference } from 'src/app/interfaces/IReportFirstPreference';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import { ITableHeader } from 'src/app/interfaces/ITableHeader';
import { ElectionStatusEnum } from 'src/app/enums/ElectionStatusEnum';
import { LcQuotasDataService } from 'src/app/services/lc-quotas-data.service';

@Component({
  selector: 'lc-quota-won-table',
  templateUrl: './lc-quota-won-table.component.html',
  styleUrls: ['./lc-quota-won-table.component.scss']
})
export class LcQuotaWonTableComponent implements AfterViewInit, OnDestroy {

  @Input() isSmallTable = true;
  @Input() title = 'First Preference Votes';

  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

  public LCChangePartyData: any[] = [];
  public independentDetailsQuota: any[] = [];
  public isIndependentsExpanded = false;

  public dataTable: IReportFirstPreference[] = [];
  public dataSource = new MatTableDataSource<IReportFirstPreference>();
  public columns: ITableHeader[] = [];
  public displayedColumns: string[] = [];
  public dataIsLoaded = true;
  public isFinal = false;
  public quotaVotes: number | null = null;

  constructor(
    private electionService: ElectionService,
    private lcQuotasDataService: LcQuotasDataService
  ) {
    this.electionService.dataLoaded$.pipe(takeUntil(this.componentDestroyed$)).subscribe(dataLoaded => {
      if (this.isDataLoaded(dataLoaded.electionDate)) {
        this.loadChart(this.electionService.selectedPage);
      }
    });
    this.electionService.pageChanged$.pipe(takeUntil(this.componentDestroyed$)).subscribe(page => {
      this.loadChart(page);
    });
  }

  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic
      ]);
    }
    return false;
  }

  ngAfterViewInit(): void {
    if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
      this.loadChart(this.electionService.selectedPage);
    }
  }

  private loadChart(page: ISelectPage): void {
    this.columns = [];
    this.LCChangePartyData = [];
    this.independentDetailsQuota = [];
    const election = this.electionService.electionMap.get(page.electionDateString ?? '');

    this.isFinal = election?.haChange?.electionStatus === ElectionStatusEnum.final;

    if (this.isFinal) {
      this.quotaVotes = election?.lcElectionCount?.quotaVotes ? Math.trunc(election?.lcElectionCount?.quotaVotes) : null;
    }

    const electionYear = election?.lcElectionCount?.electionYear || '';
    const previousElectionYear = this.lcQuotasDataService.getPreviousElectionYear(electionYear);

    if (page.pageType === PageTypeEnum.CountLCStateWide) {
      // Check if we should load from CSV (when final) or use API data
      const shouldUseCSV = this.isFinal && this.shouldLoadFromCSV(election);
      
      if (shouldUseCSV) {
        console.log('Loading LC Quotas table data from CSV for election:', page.electionDateString);
        this.loadDataFromCSV(page.electionDateString ?? '', electionYear, previousElectionYear);
      } else {
        // Use existing API data
        this.loadDataFromAPI(election, electionYear, previousElectionYear);
      }
    }
  }

  /**
   * Check if we should load data from CSV
   * Returns true if election is final and we don't have complete API data
   */
  private shouldLoadFromCSV(election: any): boolean {
    // If no election data or no LC data, use CSV
    if (!election || !election.lcElectionCount) {
      return true;
    }
    
    // If we have API data with parties, check if it's complete
    const parties = election.lcElectionCount.parties || [];
    if (parties.length === 0) {
      return true;
    }
    
    // Check if we have seats won data in API
    const hasSeatsData = parties.some((p: any) => p.seatsWon > 0);
    
    // If no seats data in API, use CSV
    return !hasSeatsData;
  }

  /**
   * Load data from CSV file
   */
  private loadDataFromCSV(electionDateString: string, electionYear: string, previousElectionYear: string): void {
    const election = this.electionService.electionMap.get(electionDateString);
    const staticParties = election?.lcStatic?.parties || [];
    
    this.lcQuotasDataService.loadCompleteQuotasData$(electionDateString, staticParties)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        (data) => {
          if (data && data.length > 0) {
            console.log('Successfully loaded LC Quotas data from CSV:', data.length, 'parties');
            
            // Map CSV data to table format
            this.LCChangePartyData = data.map(row => ({
              district: null,
              partyId: row.partyId,
              party: row.partyName,
              colourCode: row.colourCode,
              candidateName: null,
              votes: row.quotasWon.toFixed(1),
              seatsWon: row.seatsWon,
              previousElectionSeatsWon: row.seatsPreviousElection,
              change: row.change
            }));
            
            this.setupColumns(electionYear || '2022', previousElectionYear || '2018');
            this.finalizeDataSource();
          } else {
            console.warn('No quota data found in CSV');
            this.loadDataFromAPI(election, electionYear || '2022', previousElectionYear || '2018');
          }
        },
        (error) => {
          console.error('Error loading quota data from CSV:', error);
          this.loadDataFromAPI(election, electionYear || '2022', previousElectionYear || '2018');
        }
      );
  }

  /**
   * Load data from API (existing logic). Groups IND parties into one "Independents" row and guards against NaN.
   */
  private loadDataFromAPI(election: any, electionYear: string, previousElectionYear: string): void {
    const previousElectionResult = election?.lcElectionCount?.previousElectionResult;
    const allParties = election?.lcElectionCount?.parties ?? [];

    const { independents, others } = allParties.reduce(
      (acc: { independents: any[]; others: any[] }, party: any) => {
        if (party.partyId === 'IND') {
          acc.independents.push(party);
        } else {
          acc.others.push(party);
        }
        return acc;
      },
      { independents: [] as any[], others: [] as any[] }
    );

    const mapPartyRow = (party: any) => {
      const prevParty = previousElectionResult?.parties?.find((x: any) => x.id == party.id);
      const prevSeats = prevParty?.seats ?? 0;
      const quotaPercent = Number(party.quotaPercent);
      const safeQuota = Number.isFinite(quotaPercent) ? quotaPercent.toFixed(1) : '0.0';
      return {
        district: null,
        partyId: party.partyId,
        party: party.partyName,
        colourCode: party.colourCode,
        candidateName: null,
        votes: safeQuota,
        seatsWon: party.seatsWon ?? 0,
        previousElectionSeatsWon: prevSeats,
        change: this.calculateChange(party.seatsWon ?? 0, prevSeats)
      };
    };

    const nonIndependentRows = others.map((party: any) => mapPartyRow(party));
    this.independentDetailsQuota = independents.map((party: any) => mapPartyRow(party));

    const independentRow =
      independents.length > 0
        ? [{
            district: null,
            partyId: 'IND',
            party: 'Independents',
            colourCode: independents[0].colourCode,
            candidateName: null,
            votes: (() => {
              const sum = independents.reduce((s: number, p: any) => s + (Number(p.quotaPercent) || 0), 0);
              return Number.isFinite(sum) ? sum.toFixed(1) : '0.0';
            })(),
            seatsWon: independents.reduce((s: number, p: any) => s + (p.seatsWon ?? 0), 0),
            previousElectionSeatsWon: independents.reduce((s: number, p: any) => {
              const prev = previousElectionResult?.parties?.find((x: any) => x.id == p.id);
              return s + (prev?.seats ?? 0);
            }, 0),
            change: (() => {
              const seatsWon = independents.reduce((s: number, p: any) => s + (p.seatsWon ?? 0), 0);
              const prevSeats = independents.reduce((s: number, p: any) => {
                const prev = previousElectionResult?.parties?.find((x: any) => x.id == p.id);
                return s + (prev?.seats ?? 0);
              }, 0);
              return this.calculateChange(seatsWon, prevSeats);
            })()
          }]
        : [];

    this.LCChangePartyData = [...nonIndependentRows, ...independentRow];
    this.LCChangePartyData.sort((a, b) => (Number(b.votes) || 0) - (Number(a.votes) || 0));
    this.isIndependentsExpanded = false;
    this.setupColumns(electionYear, previousElectionYear);
    this.finalizeDataSource();
  }

  /**
   * Setup table columns
   */
  private setupColumns(electionYear: string, previousElectionYear: string): void {
    this.columns = [];
    
    this.columns.push({
      column: 'colourCode',
      label: '',
      labelTwo: '',
      isTwoRowLabel: false,
      minWidth: 20,
      isBold: false,
      isRightJustify: false,
      isPercent: false,
      isColour: true,
      isColourBar: false,
      colourCode: null
    });
    this.columns.push({
      column: 'party',
      label: 'Party',
      labelTwo: '',
      isTwoRowLabel: false,
      minWidth: 60,
      isBold: false,
      isRightJustify: false,
      isPercent: false,
      isColour: false,
      isColourBar: false,
      colourCode: null
    });
    this.columns.push({
      column: 'votes',
      label: 'Quotas',
      labelTwo: 'Won',
      isTwoRowLabel: true,
      minWidth: 60,
      isBold: false,
      isRightJustify: true,
      isPercent: false,
      isColour: false,
      isColourBar: false,
      colourCode: null
    });
    this.columns.push({
      column: 'seatsWon',
      label: `Seats`,
      labelTwo: `${electionYear}`,
      isTwoRowLabel: true,
      minWidth: 60,
      isBold: false,
      isRightJustify: true,
      isPercent: false,
      isColour: false,
      isColourBar: false,
      colourCode: null
    });
    this.columns.push({
      column: 'previousElectionSeatsWon',
      label: `Seats`,
      labelTwo: `${previousElectionYear}`,
      isTwoRowLabel: true,
      minWidth: 60,
      isBold: false,
      isRightJustify: true,
      isPercent: false,
      isColour: false,
      isColourBar: false,
      colourCode: null
    });
    this.columns.push({
      column: 'change',
      label: 'Change',
      labelTwo: '',
      isTwoRowLabel: false,
      minWidth: 60,
      isBold: false,
      isRightJustify: true,
      isPercent: false,
      isColour: false,
      isColourBar: false,
      colourCode: null
    });
  }

  /**
   * Finalize the data source after loading data
   */
  private finalizeDataSource(): void {
    this.displayedColumns =
      this.independentDetailsQuota.length > 0
        ? ['expand', ...this.columns.map(c => c.column)]
        : this.columns.map(c => c.column);
    this.updateQuotaDataSource();
  }

  toggleIndependents(): void {
    this.isIndependentsExpanded = !this.isIndependentsExpanded;
    this.updateQuotaDataSource();
  }

  private updateQuotaDataSource(): void {
    if (this.isIndependentsExpanded && this.independentDetailsQuota.length > 0) {
      const idx = this.LCChangePartyData.findIndex((r: any) => r.partyId === 'IND' && r.party === 'Independents');
      if (idx !== -1) {
        const updated = [...this.LCChangePartyData];
        updated.splice(idx + 1, 0, ...this.independentDetailsQuota);
        this.dataTable = updated;
      } else {
        this.dataTable = this.LCChangePartyData;
      }
    } else {
      this.dataTable = this.LCChangePartyData;
    }
    if (this.dataTable?.length > 0) {
      this.dataSource = new MatTableDataSource<IReportFirstPreference>(this.dataTable);
    }
  }

  isIndependentsRow(element: any): boolean {
    return element.partyId === 'IND' && element.party === 'Independents';
  }

  isIndividualIndependent(element: any): boolean {
    return (
      element.partyId === 'IND' &&
      element.party !== 'Independents' &&
      this.independentDetailsQuota.some((ind: any) => ind.party === element.party)
    );
  }

  private calculateChange(seatsWon: number, previousElectionResults: number | undefined): string {
    const prev = previousElectionResults ?? 0;
    const change = (seatsWon ?? 0) - prev;
    if (change > 0) {
      return `+${change}`;
    }
    return change.toString();
  }

  isNumberType = (val: number | string): boolean => typeof val === 'number';

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
